import {
  Box,
  IconButton,
  keyframes,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useState, useRef, useCallback, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/util/logger';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import { useChatStore } from '@/hooks/useChatStore';
import { useProfile } from '@/hooks/useProfile';
import WavesFormBarsAnimation from './WavesFormBarsAnimation';
import { useAuth } from '@/hooks/useAuth';
import MicOffOutlinedIcon from '@mui/icons-material/MicOffOutlined';
import MicIcon from '@mui/icons-material/Mic';
import {
  CreateConversationPayload,
  CreateConversationResponse,
  CreateVoiceMessagePayload,
  Message,
} from '@/interface/chat';
import { useApiMutation } from '@/hooks/useApi';
import { useFastApiUrl } from '@/util/fastApiUrl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  isStaffMode,
  generateStaffConversationId,
  generateStaffMessageId,
} from '@/util/staffUtils';
type VoiceUIState = 'idle' | 'processing' | 'listening';

interface VoiceStateAction {
  type: 'SET_IDLE' | 'SET_PROCESSING' | 'SET_LISTENING' | 'SET_STATE';
  payload?: VoiceUIState;
}

const voiceStateReducer = (
  state: VoiceUIState,
  action: VoiceStateAction,
): VoiceUIState => {
  switch (action.type) {
    case 'SET_IDLE':
      return 'idle';
    case 'SET_PROCESSING':
      return 'processing';
    case 'SET_LISTENING':
      return 'listening';
    case 'SET_STATE':
      return action.payload || 'idle';
    default:
      return state;
  }
};

const blinkScale = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
`;

export default function WebRTCVoiceChat() {
  const baseUrl = useFastApiUrl();
  const theme = useTheme();
  const pathname = useLocation();
  const chatId = pathname.pathname.split('/')[2] || null;
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const navigate = useNavigate();
  const { setVoiceMode, voiceMode, setChatHistory, chatHistory } =
    useChatStore();
  const { profile } = useProfile();
  const { t } = useTranslation(['voiceMode']);

  const [voiceUIState, dispatchVoiceState] = useReducer(
    voiceStateReducer,
    'idle',
  );

  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [isMuted, setIsMuted] = useState(false);
  const [shouldCreateMessage, setShouldCreateMessage] = useState(false);
  const isMutedRef = useRef(isMuted);
  const chatHistoryRef = useRef<Message[]>(chatHistory);
  const conversationIdRef = useRef<string | null>(chatId);
  const conversationSummaryRef = useRef<string | null>(null);
  const createMessageRef = useRef<CreateVoiceMessagePayload | null>(null);
  const createMessageTextRef = useRef<string | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const OAI_REALTIME_BASE = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const ephemeralTokenRef = useRef<string | null>(null);
  const webrtcSessionUpdateRef = useRef<any>(null);
  const uiInstructionsRef = useRef<string | null>(null);
  const originalTurnDetectionRef = useRef<any>(null);
  const lastUserTranscriptRef = useRef<string | null>(null);
  const assistantResponseBufferRef = useRef<string>('');
  const assistantSpeakingRef = useRef<boolean>(false);
  const isProcessingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  const { mutateAsync: createConversation } = useApiMutation<
    CreateConversationResponse,
    CreateConversationPayload
  >({
    url: '/user/createConversation',
    method: 'post',
  });
  const { mutateAsync: createMessage } = useApiMutation<
    any,
    CreateVoiceMessagePayload
  >({
    url: '/user/createMessage',
    method: 'post',
  });

  const getUIMessage = (state: VoiceUIState): string => {
    switch (state) {
      case 'idle':
        return isMuted ? t('muteText') : t('speakNaturally');
      case 'processing':
        return t('processingMessage') + '...';
      case 'listening':
        return t('listeningToAI');
      default:
        return t('speakNaturally');
    }
  };

  const getConnectionStatusText = (status: string): string => {
    switch (status) {
      case 'Disconnected':
        return t('disconnected');
      case 'Connecting...':
        return t('connecting');
      case 'Authenticating...':
        return t('authenticating');
      case 'Signaling Error':
        return t('signalingError');
      case 'Ready':
        return t('live');
      default:
        return status;
    }
  };

  const getAnimationState = ():
    | 'speaking'
    | 'listening'
    | 'processing'
    | 'default' =>
    voiceUIState === 'listening'
      ? 'speaking'
      : voiceUIState === 'processing'
        ? 'processing'
        : 'default';

  const updateVoiceUIState = useCallback(() => {
    if (assistantSpeakingRef.current) {
      dispatchVoiceState({ type: 'SET_LISTENING' });
    } else if (isProcessingRef.current) {
      dispatchVoiceState({ type: 'SET_PROCESSING' });
    } else {
      dispatchVoiceState({ type: 'SET_IDLE' });
    }
  }, []);

  useEffect(() => {
    updateVoiceUIState();
  }, [updateVoiceUIState]);

  const teardownPeer = useCallback(() => {
    try {
      dcRef.current?.close();
    } catch {}
    try {
      pcRef.current?.close();
    } catch {}
    dcRef.current = null;
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
  }, []);

  const disconnect = useCallback(
    async (shouldCloseView = true) => {
      const conversationId = conversationIdRef.current;
      hasInitializedRef.current = false;
      isProcessingRef.current = false;
      assistantSpeakingRef.current = false;
      conversationSummaryRef.current = null;
      conversationIdRef.current = null;
      createMessageRef.current = null;
      createMessageTextRef.current = null;
      OAI_REALTIME_BASE.current = null;

      teardownPeer();

      sessionIdRef.current = null;
      ephemeralTokenRef.current = null;
      webrtcSessionUpdateRef.current = null;

      setConnectionStatus(t('disconnected'));
      setIsMuted(false);
      dispatchVoiceState({ type: 'SET_IDLE' });
      if (shouldCloseView) {
        setVoiceMode(false);
        if (conversationId) {
          // In staff mode, skip backend query invalidation
          if (!isStaffMode()) {
            queryClient.invalidateQueries({
              queryKey: [
                '/user/getAllConversation',
                profile?.value[0].eXpMember.Uuid as string,
              ],
            });
          }
          navigate(`/chat/${conversationId}`);
        }
      }
    },
    [setVoiceMode],
  );

  const createRealtimeSession = useCallback(async () => {
    if (!profile) throw new Error('Missing profile');
    const jwt = authState?.idToken?.idToken;
    if (!jwt) throw new Error('Missing JWT');

    const res = await fetch(`${baseUrl}/api/realtime/create-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        user_uuid: profile.value[0].eXpMember.Uuid,
        username: `${profile.value[0].MemberFirstName} ${profile.value[0].MemberLastName}`,
      }),
    });
    if (!res.ok) throw new Error(`create-session failed: ${res.status}`);
    const data = await res.json();
    OAI_REALTIME_BASE.current = data.webrtc_config.base_url;
    sessionIdRef.current = data.session_id;
    ephemeralTokenRef.current = data.ephemeral_token;
    webrtcSessionUpdateRef.current = data.webrtc_config?.session_update;
    uiInstructionsRef.current = data.webrtc_config?.ui_instructions ?? null;
    return data;
  }, [authState?.idToken?.idToken, profile]);

  const muteMicrophone = useCallback(() => {
    localStreamRef.current
      ?.getAudioTracks()
      .forEach((t) => (t.enabled = false));
    try {
      dcRef.current?.send(
        JSON.stringify({
          type: 'session.update',
          session: {
            turn_detection: { type: 'none' },
          },
        }),
      );
    } catch {}
  }, []);

  const unmuteMicrophone = useCallback(() => {
    localStreamRef.current?.getAudioTracks().forEach((t) => (t.enabled = true));
    try {
      const restoreTurnDetection = originalTurnDetectionRef.current ??
        webrtcSessionUpdateRef.current?.session?.turn_detection ?? {
          type: 'server_vad',
          threshold: 0.5,
          silence_duration_ms: 200,
        };

      dcRef.current?.send(
        JSON.stringify({
          type: 'session.update',
          session: {
            turn_detection: restoreTurnDetection,
          },
        }),
      );
    } catch {}
  }, []);

  const handleMuteToggle = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      if (next) {
        muteMicrophone();
      } else {
        unmuteMicrophone();
      }
      return next;
    });
  }, [muteMicrophone, unmuteMicrophone]);

  const handleOaiEvent = useCallback(
    async (evt: any) => {
      switch (evt?.type) {
        case 'session.created': {
          try {
            originalTurnDetectionRef.current =
              evt?.session?.turn_detection ?? originalTurnDetectionRef.current;
          } catch {}
          break;
        }
        case 'conversation.item.input_audio_transcription.completed': {
          const transcript: string = evt?.transcript ?? '';
          if (!transcript?.trim()) break;
          isProcessingRef.current = true;

          lastUserTranscriptRef.current = transcript.trim();
          try {
            const jwt = authState?.idToken?.idToken;
            if (!jwt || !sessionIdRef.current) break;
            dispatchVoiceState({ type: 'SET_PROCESSING' });
            const conversationHistory = chatHistoryRef.current
              .slice(-3)
              .map((message: Message) => ({
                question: message.messageContent,
                answer: message.response,
                sql_query: message.sql_query,
                table_used: message.table_used,
              }));
            const resp = await fetch(
              `${baseUrl}/api/realtime/process-user-input`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify({
                  session_id: sessionIdRef.current,
                  transcript: transcript.trim(),
                  user_uuid: profile?.value[0].eXpMember.Uuid,
                  username: `${profile?.value[0].MemberFirstName} ${profile?.value[0].MemberLastName}`,
                  is_first_message: !conversationIdRef.current ? true : false,
                  conversation_history: conversationHistory,
                }),
              },
            );

            if (resp.ok) {
              const result = await resp.json();
              dispatchVoiceState({ type: 'SET_LISTENING' });
              if (result?.type === 'business' && result?.success) {
                const dc = dcRef.current;
                if (dc && dc.readyState === 'open') {
                  dc.send(JSON.stringify({ type: 'response.cancel' }));

                  const text = String(result.data.answer ?? '').trim();
                  assistantResponseBufferRef.current = text;
                  const id = `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
                  dc.send(
                    JSON.stringify({
                      type: 'conversation.item.create',
                      item: {
                        id,
                        type: 'message',
                        role: 'user',
                        content: [{ type: 'input_text', text }],
                      },
                    }),
                  );
                  dc.send(
                    JSON.stringify({
                      type: 'response.create',
                      response: {
                        instructions: `${uiInstructionsRef.current} else: ${text}`,
                      },
                    }),
                  );
                }
                if (result?.data && profile?.value?.[0]?.eXpMember?.Uuid) {
                  createMessageTextRef.current = lastUserTranscriptRef.current;
                  createMessageRef.current = {
                    userId: profile.value[0].eXpMember.Uuid,
                    conversationId: conversationIdRef.current!,
                    messageContent: lastUserTranscriptRef.current || '',
                    messageType: 'text',
                    deviceType: 'Desktop',
                    isUpdatedConversation: false,
                    username: `${profile.value[0].MemberFirstName} ${profile.value[0].MemberLastName}`,
                    chatType: 'Voice Mode',
                    response:
                      result.data.answer_html ||
                      result.data.answer ||
                      result.response ||
                      '',
                    deepLink: result.data.deeplink || '',
                    sql_query: result.data.sql_query || '',
                    table_used: result.data.table_used || '',
                    sql_success: result.data.sql_success || false,
                  };
                  conversationSummaryRef.current =
                    result.data.conversation_summary || '';
                  setShouldCreateMessage(true);
                }
              }
            }
          } catch {
          } finally {
            isProcessingRef.current = false;
          }
          break;
        }
        case 'response.started':
          assistantSpeakingRef.current = true;
          break;
        case 'output_audio_buffer.stopped':
          if (isProcessingRef.current === false) {
            dispatchVoiceState({ type: 'SET_IDLE' });
          }
          assistantSpeakingRef.current = false;
          break;

        case 'response.output_text.delta':
          if (typeof evt.delta === 'string') {
            assistantResponseBufferRef.current += evt.delta;
          }
          break;
        case 'response.output_item.done': {
          logger.info({
            message: 'answer spoken',
            context: { transcript: evt?.item?.content?.[0]?.transcript },
          });
          break;
        }
        case 'error':
          logger.error({ message: '[Realtime error]', error: evt });
          break;

        default:
          break;
      }
    },
    [voiceUIState],
  );

  const connectRealtime = useCallback(async () => {
    if (!profile) return;
    if (pcRef.current) return;

    setConnectionStatus(t('connecting'));
    const session = await createRealtimeSession();
    const pc = new RTCPeerConnection({
      iceServers: session?.webrtc_config?.ice_servers,
    });
    pcRef.current = pc;
    pc.ontrack = (e) => {
      if (!audioElRef.current) {
        const el = new Audio();
        el.autoplay = true;
        audioElRef.current = el;
      }
      if (audioElRef.current) {
        audioElRef.current.srcObject = e.streams[0];
        audioElRef.current.play().catch(() => {});
      }
    };
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 24000,
      },
    });
    localStreamRef.current = stream;
    const [track] = stream.getAudioTracks();
    pc.addTrack(track, stream);
    const dc = pc.createDataChannel('oai-events');
    dcRef.current = dc;

    dc.onopen = () => {
      setConnectionStatus(t('authenticating'));
      if (webrtcSessionUpdateRef.current) {
        dc.send(JSON.stringify(webrtcSessionUpdateRef.current));
      }
      setConnectionStatus(t('live'));
    };

    dc.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data);
        handleOaiEvent(evt);
      } catch {}
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const resp = await fetch(
      `${OAI_REALTIME_BASE.current + '/calls'}?model=${encodeURIComponent(
        session?.webrtc_config?.model,
      )}`,
      {
        method: 'POST',
        body: offer.sdp as any,
        headers: {
          Authorization: `Bearer ${ephemeralTokenRef.current}`,
          'Content-Type': 'application/sdp',
        },
      },
    );
    if (!resp.ok) throw new Error(`SDP exchange failed: ${resp.status}`);

    const answer: RTCSessionDescriptionInit = {
      type: 'answer',
      sdp: await resp.text(),
    };
    await pc.setRemoteDescription(answer);
  }, [createRealtimeSession, handleOaiEvent]);

  useEffect(() => {
    if (voiceMode && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      connectRealtime().catch((e) => {
        logger.error({ message: 'Realtime connect error', error: e });
        setConnectionStatus(t('signalingError'));
      });
    }
    return () => {
      if (!voiceMode && hasInitializedRef.current) {
        disconnect(false);
      }
    };
  }, [voiceMode]);

  useEffect(() => {
    const sendMessage = async () => {
      if (!createMessageRef.current || !profile) return;

      const isStaff = isStaffMode();

      // In staff mode, skip all backend API calls — only update Redux state
      if (isStaff) {
        if (!conversationIdRef.current) {
          conversationIdRef.current = generateStaffConversationId();
        }
        const dummyId = generateStaffMessageId();
        const now = new Date().toISOString();
        setShouldCreateMessage(false);
        const newMessage: Message = {
          uId: dummyId,
          expiredOn: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          updatedOn: now,
          queryResponse: createMessageRef.current.response || '',
          id: dummyId,
          chatType: 'Voice Mode',
          conversationId: conversationIdRef.current!,
          createdOn: now,
          messageType: 'text',
          messageContent: createMessageRef.current.messageContent,
          response: createMessageRef.current.response || '',
          userId: profile.value[0].eXpMember.Uuid,
          messageId: dummyId,
          feedback: undefined,
          deepLink:
            createMessageRef.current.deeplink ||
            createMessageRef.current.deepLink ||
            '',
          sql_query: createMessageRef.current.sql_query,
          table_used: createMessageRef.current.table_used,
        };
        setChatHistory([...chatHistory, newMessage]);
        createMessageRef.current = null;
        createMessageTextRef.current = null;
        return;
      }

      if (!conversationIdRef.current) {
        try {
          const { data } = await createConversation({
            userId: profile.value[0].eXpMember.Uuid,
            chatType: 'Voice Mode',
            conversationSummary: conversationSummaryRef.current || 'Voice Mode',
            userEmail: profile.value[0].MemberEmail as string,
            userName:
              `${profile.value[0].MemberFirstName} ${profile.value[0].MemberLastName}` ||
              '',
          });
          conversationIdRef.current = data.conversationId;
        } catch (err) {
          logger.error({ message: 'Error creating conversation', error: err });
          return;
        }
      }
      try {
        const data = await createMessage({
          ...createMessageRef.current,
          conversationId: conversationIdRef.current!,
          userEmail: profile.value[0].MemberEmail,
          userName:
            `${profile.value[0].MemberFirstName} ${profile.value[0].MemberLastName}` ||
            '',
        });
        setShouldCreateMessage(false);
        const newMessage: Message = {
          uId: data.data.uId,
          expiredOn: data.data.expireAt,
          updatedOn: data.data.updatedOn,
          queryResponse: data.data.response,
          id: data.data.id,
          chatType: data.data.chatType,
          conversationId: data.data.conversationId,
          createdOn: data.data.createdOn,
          expireAt: data.data.expireAt,
          messageType: 'text',
          messageContent: data.data.messageContent,
          response: data.data.response,
          userId: data.data.userId,
          messageId: data.data.id,
          feedback: undefined,
          deepLink:
            createMessageRef.current.deeplink ||
            createMessageRef.current.deepLink ||
            '',
          sql_query: createMessageRef.current.sql_query,
          table_used: createMessageRef.current.table_used,
        };
        setChatHistory([...chatHistory, newMessage]);
        createMessageRef.current = null;
        createMessageTextRef.current = null;
      } catch (err) {
        logger.error({ message: 'Error creating message', error: err });
      }
    };
    if (shouldCreateMessage) {
      sendMessage();
    }
  }, [shouldCreateMessage]);
  const isConnected = connectionStatus === t('live');

  return (
    <Box
      data-testid="voice-mode-container"
      sx={{
        width: '100%',
        height: {
          lg: 'calc(100dvh)',
          xs: 'calc(100dvh)',
        },
        [theme.breakpoints.down('lg')]: { paddingTop: '80px' },
        background:
          'linear-gradient(to bottom, #325ba5, #4066ab, #5073b1, #5576b2, #607eb6)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.2rem 0',
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        data-testid="connection-status"
      >
        <FiberManualRecordIcon
          color="error"
          data-testid="connection-indicator"
          sx={{
            fontSize: '.8rem',
            animation: isConnected
              ? `${blinkScale} 1.4s ease-in-out infinite`
              : 'none',
          }}
        />
        <Typography
          data-testid="connection-status-text"
          sx={{
            fontSize: '1rem',
            color: 'white',
            fontWeight: 600,
            letterSpacing: '0.04rem',
          }}
        >
          {isConnected
            ? isMuted
              ? t('muted')
              : t('live')
            : getConnectionStatusText(connectionStatus)}
        </Typography>
      </Box>

      <Box
        data-testid="voice-mode-content"
        sx={{
          textAlign: 'center',
          width: '100%',
          px: 2,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {!isConnected && (
          <Box data-testid="connection-loading">
            <CircularProgress
              size={60}
              thickness={4}
              sx={{ color: 'white' }}
              data-testid="connection-spinner"
            />
            <Typography
              mt={2}
              sx={{ color: 'white', fontWeight: 400, fontSize: '1.2rem' }}
              data-testid="connection-loading-text"
            >
              {getConnectionStatusText(connectionStatus)}
            </Typography>
          </Box>
        )}
        {isConnected && (
          <Box
            data-testid="voice-mode-active"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <WavesFormBarsAnimation
              state={getAnimationState()}
              isMuted={isMuted}
              data-testid="voice-animation"
            />
            <Typography
              data-testid="voice-mode-message"
              sx={{
                color: 'white',
                fontWeight: 400,
                fontSize: '1.2rem',
                textAlign: 'center',
              }}
            >
              {getUIMessage(voiceUIState)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        data-testid="voice-controls"
        sx={{
          display: 'flex',
          gap: '1.5rem',
          alignItems: 'center',
          height: '56px',
        }}
      >
        {isConnected && (
          <>
            <IconButton
              data-testid="mute-toggle-button"
              sx={{
                backgroundColor: isMuted
                  ? '#ff3b30'
                  : 'rgba(255, 255, 255, 0.2)',
                width: 56,
                height: 56,
                '&:hover': {
                  backgroundColor: isMuted
                    ? '#ff1f1f'
                    : 'rgba(255, 255, 255, 0.3)',
                },
              }}
              onClick={handleMuteToggle}
            >
              {isMuted ? (
                <MicOffOutlinedIcon
                  sx={{ fontSize: '1.5rem', color: 'white' }}
                  data-testid="mic-off-icon"
                />
              ) : (
                <MicIcon
                  sx={{ fontSize: '1.5rem', color: 'white' }}
                  data-testid="mic-on-icon"
                />
              )}
            </IconButton>
          </>
        )}
        <IconButton
          data-testid="disconnect-button"
          sx={{
            backgroundColor: '#ff3b30',
            width: 56,
            height: 56,
            '&:hover': { backgroundColor: '#ff1f1f' },
          }}
          onClick={() => disconnect(true)}
        >
          <CloseIcon
            sx={{ fontSize: '1.5rem', color: 'white' }}
            data-testid="close-icon"
          />
        </IconButton>
      </Box>
      <audio
        ref={audioElRef as any}
        autoPlay
        style={{ display: 'none' }}
        data-testid="voice-audio-element"
      />
    </Box>
  );
}
